import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Message } from '@/types';
import { MessageItem } from './MessageItem';
import { useTranslation } from '@/i18n';

interface MessageListProps {
  messages: Message[];
  onExecutePlan?: (planContent: string, originalQuestion: string) => void;
  onSchedulePrompt?: (promptText: string, resultPreview: string) => void;
  onClearAndRetry?: (originalQuestion: string) => void;
}

export const MessageList: React.FC<MessageListProps> = ({ messages, onExecutePlan, onSchedulePrompt, onClearAndRetry }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showJump, setShowJump] = useState(false);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const [isUserSelecting, setIsUserSelecting] = useState(false);
  const lastMessageCountRef = useRef(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const selectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced scroll handler to prevent excessive re-renders
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
      setShowJump(!nearBottom);
      
      // Detect manual scroll (not at bottom)
      if (!nearBottom && messages.length > 0) {
        setUserHasScrolled(true);
      } else {
        setUserHasScrolled(false);
      }
    }, 100); // Debounce scroll events
  }, [messages.length]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  // Detect text selection to prevent auto-scroll interference
  useEffect(() => {
    const handleMouseDown = () => {
      setIsUserSelecting(true);
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };

    const handleMouseUp = () => {
      // Keep selection state active while text is selected
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
      selectionTimeoutRef.current = setTimeout(() => {
        // Only clear if nothing is actually selected
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed) {
          setIsUserSelecting(false);
        }
      }, 2000);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousedown', handleMouseDown);
      container.addEventListener('mouseup', handleMouseUp);
      return () => {
        container.removeEventListener('mousedown', handleMouseDown);
        container.removeEventListener('mouseup', handleMouseUp);
        if (selectionTimeoutRef.current) {
          clearTimeout(selectionTimeoutRef.current);
        }
      };
    }
  }, []);

  useEffect(() => {
    // Only auto-scroll when a NEW message is added (not on every re-render)
    const el = messagesEndRef.current;
    if (!el) return;

    const messageCountChanged = messages.length !== lastMessageCountRef.current;
    lastMessageCountRef.current = messages.length;

    if (messageCountChanged && !userHasScrolled && !isUserSelecting) {
      requestAnimationFrame(() => {
        if (!userHasScrolled && !isUserSelecting) {
          el?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      });
    }
  }, [messages.length, userHasScrolled, isUserSelecting]);

  const handleJumpToLatest = () => {
    const el = messagesEndRef.current;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'end' });
      setUserHasScrolled(false);
    }
  };

  // Memoize previous user messages to prevent unnecessary re-renders
  const previousUserMessages = useMemo(() => {
    const map = new Map<number, string | undefined>();
    messages.forEach((message, index) => {
      if (message.type === 'assistant') {
        for (let i = index - 1; i >= 0; i--) {
          if (messages[i].type === 'user') {
            map.set(index, messages[i].content);
            break;
          }
        }
      }
    });
    return map;
  }, [messages]);

  // Helper to find the previous user message for an assistant message
  const getPreviousUserMessage = useCallback((index: number): string | undefined => {
    return previousUserMessages.get(index);
  }, [previousUserMessages]);

  // Memoize message items to prevent unnecessary re-renders
  const messageItems = useMemo(() => {
    return messages.map((message, index) => (
      <MessageItem
        key={message.id}
        message={message}
        previousUserMessage={message.type === 'assistant' ? getPreviousUserMessage(index) : undefined}
        onExecutePlan={onExecutePlan}
        onSchedulePrompt={onSchedulePrompt}
        onClearAndRetry={onClearAndRetry}
      />
    ));
  }, [messages, getPreviousUserMessage, onExecutePlan, onSchedulePrompt, onClearAndRetry]);

  return (
    <div className="relative h-full">
      <div ref={containerRef} className="space-y-6 h-full overflow-y-auto chat-scroll pr-2">
        {messageItems}
        {/* Scroll anchor at the end of messages */}
        <div ref={messagesEndRef} className="h-1" />
      </div>
      {showJump && (
        <button
          onClick={handleJumpToLatest}
          className="absolute right-4 bottom-4 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-800 dark:bg-gray-700 text-white shadow-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-all hover:scale-105"
          title={t.chat.jumpToLatest}
        >
          ↓ {t.chat.jumpToLatest}
        </button>
      )}
    </div>
  );
};